import { useEffect, useMemo, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import type { Candidate } from '@/models/job';
import { getCandidateAssessmentSummary } from '@/services/job.service';
import { isAssessedStageSemantic } from '@/utils/pipeline-stages';

export interface CandidateAssessmentMaps {
  fitScores: Record<string, number | null>;
  techScores: Record<string, number | null>;
  softSkillsScores: Record<string, number | null>;
  cheatingFlags: Record<string, boolean>;
}

export type CandidateScoresUpdatedPayload = {
  overallScores: Record<string, number | null>;
  techScores: Record<string, number | null>;
  softSkillsScores: Record<string, number | null>;
};

const EMPTY_MAPS: CandidateAssessmentMaps = {
  fitScores: {},
  techScores: {},
  softSkillsScores: {},
  cheatingFlags: {},
};

function assessedSignature(candidates: Candidate[]): string {
  return candidates
    .filter(
      (c) =>
        isAssessedStageSemantic(c.pipelineStageSemantic) ||
        c.hasAssessmentAvailable
    )
    .map((c) => c.id)
    .sort()
    .join(',');
}

export function useCandidateAssessmentScores(
  candidates: Candidate[],
  onScoresUpdated?: (scores: CandidateScoresUpdatedPayload) => void
): CandidateAssessmentMaps {
  const { getAccessTokenSilently } = useAuth0();
  const [maps, setMaps] = useState<CandidateAssessmentMaps>(EMPTY_MAPS);

  // Stable key so identity-only array changes do not cancel in-flight fetches.
  const signature = useMemo(() => assessedSignature(candidates), [candidates]);

  const assessedCandidates = useMemo(
    () =>
      candidates.filter(
        (c) =>
          isAssessedStageSemantic(c.pipelineStageSemantic) ||
          c.hasAssessmentAvailable
      ),
    // Keyed by assessed id signature so array identity churn does not refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [signature]
  );

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (assessedCandidates.length === 0) {
        if (!cancelled) setMaps(EMPTY_MAPS);
        return;
      }

      try {
        const accessToken = await getAccessTokenSilently();
        const rows = await Promise.all(
          assessedCandidates.map(async (candidate) => {
            const response = await getCandidateAssessmentSummary(
              accessToken,
              candidate.id
            );
            const data = response.data;
            if (!data) {
              return {
                id: candidate.id,
                fitScore: null,
                techScore: null,
                softSkillsScore: null,
                hasCheatingFlag: false,
              };
            }

            const tech = data.technical_assessment;
            const soft = data.soft_skills_assessment;

            return {
              id: candidate.id,
              fitScore: data.fit_score ?? null,
              techScore: tech?.overall_tech_percent ?? tech?.overall_score ?? null,
              softSkillsScore:
                soft?.overall_soft_skills_percent ?? soft?.overall_score ?? null,
              hasCheatingFlag: data.has_cheating_flag ?? false,
            };
          })
        );

        if (cancelled) return;

        const next: CandidateAssessmentMaps = {
          fitScores: {},
          techScores: {},
          softSkillsScores: {},
          cheatingFlags: {},
        };

        for (const row of rows) {
          next.fitScores[row.id] = row.fitScore;
          next.techScores[row.id] = row.techScore;
          next.softSkillsScores[row.id] = row.softSkillsScore;
          next.cheatingFlags[row.id] = row.hasCheatingFlag;
        }

        setMaps(next);
        onScoresUpdated?.({
          overallScores: next.fitScores,
          techScores: next.techScores,
          softSkillsScores: next.softSkillsScores,
        });
      } catch (error) {
        console.error('Error fetching assessment scores:', error);
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [assessedCandidates, getAccessTokenSilently, onScoresUpdated]);

  return maps;
}
